import { auth } from '../firebase.js'
import { captureException } from './sentry.js'

// Model identifiers — pinned to specific generations so an upstream
// release doesn't silently change behavior (tone, JSON quirks, max
// output) under us. Audit S-2 (2026-05-14): track upgrades by bumping
// these strings + the changelog below when Anthropic ships a new
// generation. Verify quality on each upgrade by:
//   1. Rerunning the WOTD prompt for 5 personas (review tone + JSON shape)
//   2. Spot-checking 5 Classic Analysis runs for CMS-A faithfulness
//   3. Sommelier chat smoke: ask for a cellar summary, verify it doesn't
//      hallucinate wines outside the prompt.
//
// Model changelog:
//   - 2026-05-08: Haiku pinned to claude-haiku-4-5-20251001
//   - 2026-05-08: Sonnet pinned to claude-sonnet-4-6
const DEFAULT_MODEL = 'claude-haiku-4-5-20251001'
const SONNET_MODEL = 'claude-sonnet-4-6'
const MIN_REQUEST_GAP_MS = 1200
const MAX_RETRIES = 2

// Proxy endpoint — the key never leaves the server
const PROXY_URL = '/api/anthropic'

let lastRequestAt = 0
let requestChain = Promise.resolve()
const inflight = new Map()

async function getIdToken() {
  const user = auth.currentUser
  if (!user) throw new Error('Not authenticated')
  return user.getIdToken()
}

function stripCodeFences(text) {
  return String(text || '').replace(/```json|```/gi, '').trim()
}

function extractTextFromResponse(data) {
  return (data?.content || [])
    .filter(block => block.type === 'text' && block.text)
    .map(block => block.text)
    .join('\n')
    .trim()
}

function extractJsonString(text) {
  const clean = stripCodeFences(text)
  const match = clean.match(/\{[\s\S]*\}|\[[\s\S]*\]/)
  return match ? match[0] : clean
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function stableStringify(value) {
  if (value === null || typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`
  return `{${Object.keys(value)
    .sort()
    .map(k => `${JSON.stringify(k)}:${stableStringify(value[k])}`)
    .join(',')}}`
}

function getCacheKey({ system, messages, maxTokens, model, temperature, tools }) {
  return stableStringify({ system, messages, maxTokens, model, temperature, tools })
}

async function performRequest({
  system,
  messages,
  maxTokens = 700,
  model = DEFAULT_MODEL,
  temperature = 0.2,
  tools,
} = {}) {
  const idToken = await getIdToken()

  const response = await fetch(PROXY_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      model,
      max_tokens: maxTokens,
      temperature,
      ...(system ? { system } : {}),
      ...(tools ? { tools } : {}),
      messages,
    }),
  })

  // Try JSON first; fall back to text so we can surface useful debug info when
  // the proxy returns something non-JSON (timeout HTML page, empty body, etc.)
  const rawText = await response.text().catch(() => '')
  let data = null
  if (rawText) {
    try { data = JSON.parse(rawText) } catch { /* non-JSON body */ }
  }

  if (!response.ok) {
    // Provider-health Sentry breadcrumb. Every non-OK upstream response
    // gets tagged so we can chart "Anthropic is having a bad day" as a
    // single signal feed in Sentry rather than scattered per-feature
    // exceptions. Distinguishes:
    //   - 429: our own circuit breaker tripped OR upstream rate limit
    //   - 5xx (incl 529): upstream capacity/server health
    //   - 4xx (other): client-side bug — auth, bad request shape, etc.
    // Tag once per failed call; the caller may also captureException
    // with its own feature/op tag (e.g. feature=wineDetail op=selfRate).
    if (response.status >= 500 || response.status === 529 || response.status === 429) {
      try {
        captureException(new Error(`Anthropic ${response.status}`), {
          tags: {
            feature: 'ai-provider',
            op:      response.status === 429 ? 'rate-limit' : 'upstream-degraded',
            provider:'anthropic',
            status:  String(response.status),
          },
          extra: {
            retryAfter: response.headers.get('retry-after') || null,
            source:     data?.source || null,    // 'circuit-breaker' | 'upstream-429' | 'upstream-529' | null
          },
        })
      } catch { /* sentry no-op without DSN configured — non-fatal */ }
    }

    // Bug report 2026-05-14: '"Anthropic rate limit reached" was leaking
    // upstream-vendor jargon into user-facing toasts. Replaced with a
    // brand-voice message — Vinarai is the surface the user knows.
    //
    // 429 with a Retry-After header (set by the server's circuit
    // breaker) — surface the countdown in the toast so the user
    // knows when to retry instead of guessing. 529 is Anthropic's
    // upstream-overloaded signal (capacity, not rate-limit) — same
    // user experience: tell them to try again in a moment.
    let message
    let retryAfter = 0
    if (response.status === 429 || response.status === 529) {
      const headerRetry = Number(response.headers.get('retry-after'))
      retryAfter = Number.isFinite(headerRetry) && headerRetry > 0
        ? headerRetry
        : (Number(data?.retryAfterSeconds) > 0 ? Number(data.retryAfterSeconds) : 0)
      message = retryAfter > 0
        ? `Vinarai is busy right now — please try again in ${retryAfter}s.`
        : 'Vinarai is busy right now — please try again in a moment.'
    } else {
      message = data?.error?.message || data?.error || `AI request failed (${response.status})`
    }

    const err = new Error(message)
    err.status = response.status
    err.retryAfter = retryAfter
    err.response = data
    throw err
  }

  // 200 OK but we couldn't parse a body, or body has no content array.
  // Without this guard callers hit `Cannot read properties of null (reading 'content')`
  // which is impossible to debug from the UI.
  if (!data || !Array.isArray(data.content)) {
    const err = new Error(
      data?.error?.message ||
      `Anthropic returned an unexpected response (status ${response.status}, ${rawText.length} bytes)`
    )
    err.status = response.status
    err.response = data ?? rawText.slice(0, 500)
    throw err
  }

  return data
}

async function scheduleRequest(opts) {
  const runner = async () => {
    const wait = Math.max(0, MIN_REQUEST_GAP_MS - (Date.now() - lastRequestAt))
    if (wait > 0) await sleep(wait)

    let attempt = 0
    while (true) {
      try {
        const data = await performRequest(opts)
        lastRequestAt = Date.now()
        return data
      } catch (err) {
        lastRequestAt = Date.now()
        // Bug report 2026-05-14 (Chloe): on a rate-limit hit we used to
        // auto-retry up to MAX_RETRIES=2 times with 1.5s/3s backoff,
        // turning ONE user click into THREE Anthropic calls. Combined
        // with the LabelScanner OCR-fallback chain (which itself fires
        // another Anthropic call for text-only re-scan), one failed
        // scan could fan out into 6+ upstream API hits — making the
        // rate-limit worse for everyone.
        //
        // 429s from /api/anthropic come from either (a) our per-user
        // daily cap, (b) our per-uid burst limit, (c) our global daily
        // emergency brake, or (d) Anthropic's upstream account limits.
        // NONE of these clear in 1.5-3 seconds in practice — (a) and
        // (c) reset at UTC midnight, (b) over 60s, (d) typically
        // minutes. Auto-retrying on the user's behalf just multiplies
        // the call volume against an already-throttled bucket.
        //
        // Fix: skip retries entirely for 429s. Let the error propagate
        // so the calling feature can short-circuit (LabelScanner now
        // skips its text-only Claude follow-up on 429, etc) and the
        // user sees the "busy, try again in a moment" toast once,
        // not three times.
        //
        // We keep the retry loop ONLY for non-429 transient errors
        // (502/503/504/529 — gateway / upstream / Anthropic-overloaded
        // errors that DO clear in seconds). Those still benefit from
        // the original backoff. 529 specifically is Anthropic's
        // capacity-overload signal — saw it in the wild 2026-05-14
        // during testing, hadn't been in the retry list before.
        const isTransient = err?.status === 502 || err?.status === 503 || err?.status === 504 || err?.status === 529
        if (isTransient && attempt < MAX_RETRIES) {
          // 529 needs a longer backoff than gateway errors — capacity
          // overload doesn't clear in 1.5s. Use 3s/6s instead of
          // 1.5s/3s for 529 specifically.
          const baseMs  = err?.status === 529 ? 3000 : 1500
          const backoff = baseMs * 2 ** attempt
          attempt += 1
          await sleep(backoff)
          continue
        }

        // Model fallback for sustained 529s (Anthropic capacity overload
        // 2026-05-14). When the primary model is saturated, fall back
        // ONCE to a less-capacity-pressured model — usually Haiku since
        // Sonnet is the most frequently saturated tier on Anthropic's
        // infrastructure.
        //
        // Auto-fallback policy: when the caller used SONNET_MODEL and
        // didn't explicitly set fallbackModel, default to Haiku. Every
        // Sonnet call site in the app benefits without per-site edits.
        // Callers can opt OUT by passing `fallbackModel: null`.
        //
        // The result is a slightly less verbose / less reasoning-heavy
        // answer instead of a hard failure. Haiku quality is acceptable
        // for graceful degradation — better than "Vinarai is busy"
        // toast for 60s.
        const resolvedFallback = (opts.fallbackModel === null)
          ? null   // explicit opt-out
          : (opts.fallbackModel || (opts.model === SONNET_MODEL ? DEFAULT_MODEL : null))
        if (err?.status === 529 && resolvedFallback && opts.model && resolvedFallback !== opts.model) {
          console.warn(`[aiClient] 529 on ${opts.model} — falling back to ${resolvedFallback}`)
          try {
            const fallbackOpts = { ...opts, model: resolvedFallback }
            // Don't recurse through scheduleRequest — we're already in
            // the queue. Just one direct attempt with the fallback.
            const data = await performRequest(fallbackOpts)
            lastRequestAt = Date.now()
            return data
          } catch (fallbackErr) {
            // Fallback also failed (Anthropic-wide outage) — let the
            // original 529 propagate so the circuit breaker + brand-
            // voice toast still fire correctly.
            console.warn(`[aiClient] fallback to ${resolvedFallback} also failed:`, fallbackErr?.status)
          }
        }
        throw err
      }
    }
  }

  const next = requestChain.then(runner, runner)
  requestChain = next.catch(() => {})
  return next
}

export async function callAnthropic(opts = {}) {
  const key = getCacheKey(opts)
  if (inflight.has(key)) return inflight.get(key)

  const promise = scheduleRequest(opts).finally(() => inflight.delete(key))
  inflight.set(key, promise)
  return promise
}

export async function callAnthropicText(opts) {
  const data = await callAnthropic(opts)
  return extractTextFromResponse(data)
}

export async function callAnthropicJson(opts) {
  const text = await callAnthropicText(opts)
  const jsonString = extractJsonString(text)
  try {
    return JSON.parse(jsonString)
  } catch (err) {
    const e = new Error('Anthropic JSON parse failed')
    e.rawText = text
    e.jsonString = jsonString
    throw e
  }
}

export { DEFAULT_MODEL, SONNET_MODEL, stripCodeFences, extractTextFromResponse, extractJsonString }
